import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStripe } from '@stripe/react-stripe-js';
import { useModal } from 'components/modals/ModalContext';
import { useGlobalContext } from 'GlobalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useFetchData } from 'services/api/useFetchData';
import { fetchData } from 'services/api/fetchData';
import { saveData } from 'services/api/saveData';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import ModulePriceBox from 'components/billing/ModulePriceBox';
import { formatPriceOnUserLocale } from '../billing/functions/billingUtils';
import { handleManualPayment } from '../billing/functions/handleManualPayment';
import '../../../style/scss/billing.scss';

interface ApiTierOrderModalProps {
    tokenId: number;
    onSuccess?: () => void;
};

// The rate limits per api tier, shown inside the tier cards
export const TIER_RATE_LIMITS: Record<string, { list: string, detail: string, write: string, daily: string }> = {
    free: { list: '5 / min', detail: '10 / min', write: '10 / min', daily: '100 / dag' },
    standard: { list: '50 / 10 sec', detail: '200 / 10 sec', write: '60 / min', daily: '5.000 / dag' },
    extended: { list: '300 / min', detail: '500 / min', write: '180 / min', daily: '20.000 / dag' },
};

const ApiTierOrderModal: React.FC<ApiTierOrderModalProps> = ({ tokenId, onSuccess }) => {
    const { t } = useTranslation();
    const stripe = useStripe();
    const { closeModal, revealModal } = useModal();
    const { setFloatingAlert } = useGlobalContext();
    const { userLocale } = useSettings();
    const { environmentHash, handleLogout } = useAuthContext();
    const [selectedTier, setSelectedTier] = useState<string | null>(null);
    const [buttonLoader, setButtonLoader] = useState<boolean>(false);

    // Fetch the order details of the api tiers for this token
    const { response: orderDetails, loading } = useFetchData({
        apiUrl: 'get_api_key_order_details',
        params: useMemo(() => ({ token_id: tokenId }), [tokenId])
    });

    // Reveal the modal after the order details are fetched
    useEffect(() => {
        if (orderDetails) {
            revealModal();
        }
    }, [orderDetails, revealModal]);

    // Show an alert and close when the order details cannot be fetched, for example without a subscription
    useEffect(() => {
        if (loading === 'error') {
            setFloatingAlert({ type: 'warning', message: 'billing.general.order_requires_subscription_message' });
            closeModal();
        }
    }, [loading, setFloatingAlert, closeModal]);

    // Render nothing until the order details are fetched
    if (!orderDetails) {
        return null;
    }

    // Get the order details of the selected tier
    const selectedTierDetails = orderDetails.tiers?.find((tier: any) => tier.tier === selectedTier);

    // Get the current tier of the api key with its monthly price
    const currentTier = orderDetails.current_tier ?? 'free';
    const currentTierPrice = parseFloat(orderDetails.tiers?.find((tier: any) => tier.tier === currentTier)?.flat_price ?? '0');

    // Handle the order and payment of the api tier
    const handleOrder = async () => {
        if (!selectedTier) return;

        try {
            // Start the button loader
            setButtonLoader(true);

            // Order the api tier in the backend as inactive line awaiting payment
            const response = await saveData({
                apiUrl: `post_api_key_order/${environmentHash}`,
                method: 'post',
                data: { token_id: tokenId, tier: selectedTier }
            });

            // Handle successful response
            if (response?.status === 200) {

                // Start the ideal payment when an amount is due, the tier activates after the payment
                if (response.data.start_manual_payment === true) {

                    // Fetch the main company details for the ideal billing details
                    const companyDetailsResponse = await fetchData({
                        apiUrl: `get_main_company_details/${environmentHash}`,
                        handleLogout
                    });

                    // Redirect to the ideal payment page, the loader keeps running until the redirect
                    // The current path is passed so the thank you page can link back to this menu
                    await handleManualPayment(
                        stripe,
                        environmentHash,
                        companyDetailsResponse,
                        response.data.subscription_id,
                        null,
                        response.data.new_module_line_ids,
                        null,
                        null,
                        response.data.api_token_id,
                        window.location.pathname
                    );

                    return;
                }

                // Without an amount due the tier is activated directly
                setButtonLoader(false);
                setFloatingAlert({ type: 'success', message: 'api_key.general.tier_order_activated_message' });

                // Call the on success callback
                if (onSuccess) {
                    onSuccess();
                }

                // Close the modal
                closeModal();
            }
        } catch (error) {
            console.error('Error on api tier order', error);

            // Stop the button loader
            setButtonLoader(false);

            // Show an alert that the order could not be completed
            setFloatingAlert({ type: 'danger', message: 'billing.general.order_failed_message' });
        };
    };

    return (
        <div className='module-order-modal api-tier-order-modal'>

            {/* Show the modal header with a short introduction */}
            <h3>{t('api_key.general.tier_order_modal_title')}</h3>
            <p>{t('api_key.general.tier_order_modal_description')}</p>

            {/* Show the tier cards next to each other, starting with the current tier */}
            <div className='api-tier-cards'>

                {/* Show the current tier of the api key as reference card */}
                <div className='api-tier-card current'>
                    <div className='tier-header'>
                        <h5>{t(`api_key.tier.${currentTier}`)}</h5>
                        <span className='current-tier-badge'>{t('api_key.general.tier_current_label')}</span>
                    </div>
                    <p className='tier-use-case'>{t(`api_key.tier_use_case.${currentTier}`)}</p>
                    <ul className='rate-limit-list'>
                        <li>{t('api_key.general.list_requests_label')}: {TIER_RATE_LIMITS[currentTier]?.list}</li>
                        <li>{t('api_key.general.detail_requests_label')}: {TIER_RATE_LIMITS[currentTier]?.detail}</li>
                        <li>{t('api_key.general.write_requests_label')}: {TIER_RATE_LIMITS[currentTier]?.write}</li>
                        <li>{t('api_key.general.daily_requests_label')}: {TIER_RATE_LIMITS[currentTier]?.daily}</li>
                    </ul>
                    <ModulePriceBox
                        amount={currentTierPrice}
                        unitLabel={t('api_key.general.per_api_key_label')}
                    />
                </div>

                {/* Show the selectable tier cards which are higher than the current tier */}
                {orderDetails.tiers?.filter((tier: any) => !tier.is_current_or_lower).map((tier: any) => (
                    <div
                        key={tier.tier}
                        className={`api-tier-card ${selectedTier === tier.tier ? 'selected' : ''}`}
                        onClick={() => setSelectedTier(tier.tier)}
                    >
                        <div className='tier-header'>
                            <h5>{t(`api_key.tier.${tier.tier}`)}</h5>
                        </div>
                        <p className='tier-use-case'>{t(`api_key.tier_use_case.${tier.tier}`)}</p>
                        <ul className='rate-limit-list'>
                            <li>{t('api_key.general.list_requests_label')}: {TIER_RATE_LIMITS[tier.tier]?.list}</li>
                            <li>{t('api_key.general.detail_requests_label')}: {TIER_RATE_LIMITS[tier.tier]?.detail}</li>
                            <li>{t('api_key.general.write_requests_label')}: {TIER_RATE_LIMITS[tier.tier]?.write}</li>
                            <li>{t('api_key.general.daily_requests_label')}: {TIER_RATE_LIMITS[tier.tier]?.daily}</li>
                        </ul>
                        <ModulePriceBox
                            amount={parseFloat(tier.flat_price)}
                            unitLabel={t('api_key.general.per_api_key_label')}
                        />
                    </div>
                ))}
            </div>

            {/* Show the payment explanation with the amounts for the remaining period */}
            {selectedTierDetails && parseFloat(selectedTierDetails.total_inc_vat) > 0 && (
                <p className='helper-text'>
                    {t('billing.general.module_order_payment_explanation', {
                        totalExVat: formatPriceOnUserLocale(parseFloat(selectedTierDetails.total_ex_vat), userLocale),
                        months: orderDetails.months_remaining.replace('.', ','),
                        totalIncVat: formatPriceOnUserLocale(parseFloat(selectedTierDetails.total_inc_vat), userLocale)
                    })}
                </p>
            )}

            {/* Show the credit explanation when the current tier is credited */}
            {selectedTierDetails && parseFloat(selectedTierDetails.credit_ex_vat) > 0 && (
                <p className='helper-text'>
                    {t('api_key.general.tier_upgrade_credit_explanation', {
                        credit: formatPriceOnUserLocale(parseFloat(selectedTierDetails.credit_ex_vat), userLocale)
                    })}
                </p>
            )}

            {/* The modal buttons */}
            <div className='button-row'>
                <div className='buttons-right'>
                    <SecondaryButton
                        type='button'
                        label='general.cancel'
                        size='small'
                        onClick={() => closeModal()}
                    />
                    <PrimaryButton
                        type='button'
                        label='billing.general.module_order_pay_label'
                        size='small'
                        loading={buttonLoader}
                        disabled={{ disabled: !selectedTier }}
                        onClick={handleOrder}
                    />
                </div>
            </div>
        </div>
    );
};

export default ApiTierOrderModal;
